/**
 * accessLabFormat.ts
 *
 * The words and dates an Access Labs result is shown with, shared by the
 * detail view (AccessLabDetailModal), the printed report
 * (AccessLabReportPrint) and the list rows (AccessResultStatusBadge).
 */
import type { BadgeVariant } from '@/types/common.types';
import type { AccessLabResultDetail } from '@/services/lab-orders.service';
import { formatDate, formatDateTime } from '@/lib/utils';

const STATUS_LABELS: Record<string, string> = {
  preliminary: 'Preliminary',
  final: 'Final',
  corrected: 'Corrected',
};

/**
 * "Preliminary" / "Final" / "Corrected". The server sends both the word
 * ('final') and its label ('Final'); the label wins, the word is the fallback,
 * and anything else is shown as nothing rather than a code.
 */
export function accessStatusLabel(status: string | null | undefined, label?: string | null): string | null {
  const given = (label ?? '').trim();
  if (given) return given;
  return STATUS_LABELS[(status ?? '').trim().toLowerCase()] ?? null;
}

export function isPreliminary(status: string | null | undefined, label?: string | null): boolean {
  return accessStatusLabel(status, label) === 'Preliminary';
}

export function accessStatusVariant(label: string | null): BadgeVariant {
  if (label === 'Final') return 'success';
  if (label === 'Preliminary') return 'warning';
  if (label === 'Corrected') return 'info';
  return 'neutral';
}

/**
 * The word for an abnormal flag (HL7 OBX-8), so a flagged value is never
 * marked by colour alone. H/HH read High, L/LL read Low, A/AA and any other
 * flag on a value marked abnormal read Abnormal. N or no flag reads nothing.
 */
export function accessFlagText(flag: string | null | undefined, abnormal?: boolean): string | null {
  const f = (flag ?? '').trim().toUpperCase();
  if (f === 'H' || f === 'HH' || f === '>') return 'High';
  if (f === 'L' || f === 'LL' || f === '<') return 'Low';
  if (f === 'A' || f === 'AA') return 'Abnormal';
  return abnormal ? 'Abnormal' : null;
}

/**
 * A date and time, e.g. "Oct 16, 2026, 9:05 AM" (the app's formatDateTime).
 * A birth date is a calendar day: formatDate reads a bare 'YYYY-MM-DD' as a
 * local date (parseDateValue), so it is never shifted.
 */
export { formatDateTime as formatAccessDateTime, formatDate as formatAccessBirthDate };

/** When this version was received: its own time, else when it was ordered. */
export function accessReceivedAt(detail: AccessLabResultDetail): string | null {
  const version = detail.versions.find((v) => v.id === detail.id);
  return version?.receivedAt ?? detail.orderedAt;
}

/** The sections that carry at least one value. */
export function sectionsWithValues(detail: AccessLabResultDetail): AccessLabResultDetail['sections'] {
  return detail.sections.filter((s) => s.observations.length > 0);
}

/** A version picker option: "<Status> – <received date and time>". */
export function accessVersionLabel(version: { status: string | null; statusLabel: string | null; receivedAt: string | null }): string {
  const status = accessStatusLabel(version.status, version.statusLabel) ?? 'Result';
  return `${status} – ${formatDateTime(version.receivedAt)}`;
}
